import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import backend from "../../backend";
import AuthContext from "../../AuthContext";
import { esDe } from "./pulsos";

// ─────────────────────────────────────────────────────────────────────────────
// COLA CONTABLE (28-ago-2026)
// El "pulso" del rol accountant. A diferencia de PulseNegocio (KPIs ejecutivos,
// que el contable también ve), esto es su COLA DE TRABAJO del día: tarjetas de
// TAREAS pendientes, cada una cliqueable hacia el módulo donde se resuelven.
// Visible para accountant y admin; cualquier otro rol → return null.
//
// Mismo lenguaje visual que PulseNegocio: número grande + sub-línea + semáforo
// + chevron, un fetch INDEPENDIENTE por tarjeta (si uno falla, muestra "—" y
// no bloquea al resto), grid 2 columnas en móvil / 3 en desktop.
//
// Endpoints consumidos (todos existentes, todos checkAuth(["admin","accountant"])):
//   1. Excepciones contables .... GET /libro/excepciones            → /contabilidad
//   2. F29 mes anterior ......... GET /libro/f29?periodo=YYYY-MM    → /contabilidad
//        (regla fija: el F29 vence el 12 del mes siguiente al período;
//         el backend solo emite BORRADOR, no hay flag "presentado" → pasado
//         el 12, la tarjeta se pone roja como recordatorio de declarar)
//   3. Pagos esta semana ........ GET /sii/pagos?meses=3            → /contabilidad
//   4. Cartolas por conciliar ... GET /cartola/resumen              → /contabilidad
//   5. Liquidaciones del mes .... GET /liquidacion?periodo=YYYY-MM  → /liquidaciones
//   6. Compras sin vincular ..... GET /sii/facturas?tipo=compra&periodo= → /contabilidad
//        (bandeja del conciliador: facturas de compra del mes sin OC ni OT
//         vinculada; las NC 61 no cuentan porque no se vinculan)
// ─────────────────────────────────────────────────────────────────────────────

const SEM = { green: "#2ecc71", red: "#e74c3c", amber: "#f39c12", blue: "#3498db", gray: "#8a8a8a" };

const clpC = v => {
  if (v == null || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toLocaleString("es-CL", { maximumFractionDigits: 1 })}M`;
  return `$${Math.round(n).toLocaleString("es-CL")}`;
};
const clp = v => (v == null || isNaN(v) ? "—" : `$${Math.round(Number(v)).toLocaleString("es-CL")}`);

const PulsoContable = ({ isMobile }) => {
  const auth = useContext(AuthContext);
  const navigate = useNavigate();
  const hasRole = auth?.can?.("ver_pulso_contable") || false;

  const [d, setD]   = useState({}); // key -> datos transformados (o null si falló)
  const [ld, setLd] = useState({}); // key -> bool cargando

  useEffect(() => {
    if (!hasRole) return;
    const now = new Date();
    const periodoActual = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    // Período del F29 = MES ANTERIOR (se declara al mes siguiente)
    const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const periodoF29 = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, "0")}`;

    // Cada fetch marca su loading, guarda su resultado (o null) y nunca tumba
    // a los demás — misma degradación por tarjeta que PulseNegocio.
    const run = (key, promise, transform) => {
      setLd(s => ({ ...s, [key]: true }));
      promise
        .then(res => setD(s => ({ ...s, [key]: transform(res.data) })))
        .catch(() => setD(s => ({ ...s, [key]: null })))
        .finally(() => setLd(s => ({ ...s, [key]: false })));
    };

    // 1 · Bandeja de excepciones del motor de asientos
    run("exc", backend.get("libro/excepciones"),
      data => ({ n: Array.isArray(data) ? data.length : 0 }));

    // 2 · Borrador F29 del mes anterior (código 91 = IVA determinado a pagar;
    //     TOTAL puede venir null mientras falte la tasa de PPM)
    run("f29", backend.get(`libro/f29?periodo=${periodoF29}`), data => {
      const l91 = (data?.lineas || []).find(x => x.codigo === "91");
      const lTot = (data?.lineas || []).find(x => x.codigo === "TOTAL");
      return { iva: l91 ? l91.monto : null, total: lTot ? lTot.monto : null, periodo: data?.periodo || periodoF29 };
    });

    // 3 · Pagos a proveedores: vencidas + por vencer en 7 días
    run("pagos", backend.get("sii/pagos?meses=3"), data => ({
      venc: data?.resumen?.vencidas   || { n: 0, monto: 0 },
      pv:   data?.resumen?.por_vencer || { n: 0, monto: 0 },
    }));

    // 4 · Movimientos bancarios sin conciliar (todas las cartolas cargadas)
    run("cartola", backend.get("cartola/resumen"), data => {
      const filas = Array.isArray(data) ? data : [];
      const pend = filas.reduce((s, f) => s + ((f.movimientos || 0) - (f.conciliados || 0)), 0);
      const meses = filas.filter(f => (f.movimientos || 0) > (f.conciliados || 0)).map(f => f.mes);
      return { pend, ultimoMes: meses.length ? meses.sort().slice(-1)[0] : null, hayCartolas: filas.length > 0 };
    });

    // 5 · Liquidaciones de sueldo del mes actual
    run("liq", backend.get(`liquidacion?periodo=${periodoActual}`), data => {
      const rows = Array.isArray(data) ? data : [];
      return {
        n: rows.length,
        borrador: rows.filter(r => r.estado === "borrador").length,
        aprobadas: rows.filter(r => r.estado === "aprobada").length,
        pagadas: rows.filter(r => r.estado === "pagada").length,
      };
    });

    // 6 · Facturas de compra del mes sin OC ni OT vinculada (cola del
    //     conciliador; NC 61 excluidas porque no se vinculan)
    run("vinc", backend.get(`sii/facturas?tipo=compra&periodo=${periodoActual}`), data => {
      const rows = (Array.isArray(data) ? data : []).filter(f => f.tipoDoc !== 61);
      const sin = rows.filter(f => !f.purchaseOrder && !f.workOrder);
      return { n: sin.length, total: rows.length, monto: sin.reduce((s, f) => s + (f.montoTotal || 0), 0) };
    });
  }, [hasRole]);

  if (!hasRole) return null;

  // ── tarjeta KPI (mismo patrón visual de PulseNegocio) ───────────────────────
  const Kpi = ({ icon, titulo, valor, sub, color, to, loading, urgente, title }) => (
    <div
      onClick={() => to && navigate(to)}
      className="t-02 tap-card"
      title={title || undefined}
      onMouseEnter={e => { if (!isMobile) e.currentTarget.style.borderColor = "#3a3a3a"; }}
      onMouseLeave={e => { if (!isMobile) e.currentTarget.style.borderColor = urgente ? "#5a2020" : "#2e2e2e"; }}
      style={{
        background: urgente ? "#241416" : "#242424",
        border: `1px solid ${urgente ? "#5a2020" : "#2e2e2e"}`,
        borderRadius: 12, padding: "13px 14px", cursor: "pointer",
        minWidth: 0, display: "flex", flexDirection: "column",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".06em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {icon} {titulo}
        </span>
        <span style={{ color: "#555", fontSize: 14, lineHeight: 1, flexShrink: 0 }}>›</span>
      </div>
      {loading
        ? <div style={{ height: 26, width: "60%", background: "#2e2e2e", borderRadius: 6, opacity: 0.6 }} />
        : <div style={{ fontSize: valor == null ? 14 : 24, fontWeight: 800, color: valor == null ? "#8a8a8a" : color, lineHeight: 1.1, letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{valor == null ? "—  sin datos" : valor}</div>
      }
      {sub && <div style={{ fontSize: 10.5, color: "#8a8a8a", marginTop: 4, overflow: "hidden", textOverflow: "ellipsis" }}>{sub}</div>}
    </div>
  );

  // ── datos derivados por tarjeta ─────────────────────────────────────────────
  const hoy = new Date();
  const mesActualNombre = hoy.toLocaleDateString("es-CL", { month: "long" });

  // 1 · Excepciones
  const exc = d.exc;
  const excN = exc ? exc.n : null;

  // 2 · F29: vence el 12 del mes SIGUIENTE al período (= el 12 de este mes).
  //     Pasado el 12, sin flag de presentado en el backend → rojo (recordatorio).
  const f29 = d.f29;
  const f29Vencido = hoy.getDate() > 12;
  const f29Sub = f29
    ? (f29Vencido
        ? `período ${f29.periodo} — venció el 12 de ${mesActualNombre}, declarar en sii.cl`
        : `período ${f29.periodo} — vence el 12 de ${mesActualNombre}`)
    : "borrador F29 del mes anterior —";
  const f29Color = f29 ? (f29Vencido ? SEM.red : SEM.amber) : "#fff";

  // 3 · Pagos
  const pa = d.pagos;
  const porPagarSemana = pa ? (pa.venc.monto || 0) + (pa.pv.monto || 0) : null;
  const pagosUrgente = !!(pa && pa.venc.n > 0);
  const pagosColor = pa ? (pa.venc.n > 0 ? SEM.red : porPagarSemana > 0 ? SEM.amber : SEM.green) : "#fff";

  // 4 · Cartolas
  const ca = d.cartola;
  const caSub = ca
    ? (!ca.hayCartolas
        ? "todavía no hay cartolas cargadas"
        : ca.pend > 0
          ? `movimientos bancarios sin conciliar${ca.ultimoMes ? ` · hasta ${ca.ultimoMes}` : ""}`
          : "todo conciliado — al día")
    : "conciliación bancaria —";

  // 5 · Liquidaciones
  const liq = d.liq;
  const liqPend = liq ? liq.borrador + liq.aprobadas : null; // aún no pagadas
  const liqValor = liq
    ? (liq.n === 0 ? "Sin generar" : `${liq.pagadas}/${liq.n}`)
    : "—";
  const liqSub = liq
    ? (liq.n === 0
        ? `liquidaciones de ${mesActualNombre} aún no generadas`
        : `pagadas de ${liq.n} · ${liq.borrador} en borrador · ${liq.aprobadas} por pagar`)
    : "liquidaciones de sueldo —";
  const liqColor = liq ? (liq.n === 0 ? SEM.amber : liqPend > 0 ? SEM.amber : SEM.green) : "#fff";

  // 6 · Compras sin vincular
  const vi = d.vinc;

  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, margin: "2px 0 9px" }}>
        <span style={{ fontSize: 11, fontWeight: 800, color: "#fff", textTransform: "uppercase", letterSpacing: ".07em" }}>
          Cola contable
        </span>
        <span style={{ fontSize: 10, color: "#777" }}>tareas pendientes de hoy</span>
      </div>

      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "repeat(2, minmax(0, 1fr))" : "repeat(3, minmax(0, 1fr))",
        gap: 8,
      }}>
        {/* 1 · Excepciones del motor de asientos — este pulso es su dueño (./pulsos.js) */}
        {esDe("contable", "excepciones") && (
        <Kpi icon="⚠️" titulo="Excepciones contables" to="/contabilidad?tab=libro"
          loading={ld.exc} urgente={!!(excN > 0)}
          valor={excN != null ? excN : "—"}
          color={exc ? (excN > 0 ? SEM.red : SEM.green) : "#fff"}
          sub={exc ? (excN > 0 ? "documentos que el motor no pudo contabilizar" : "bandeja limpia — todo contabilizado") : "bandeja del motor de asientos —"}
          title="Documentos que el motor de asientos no pudo contabilizar en la última corrida" />
        )}

        {/* 2 · F29 del período anterior */}
        {esDe("contable", "f29") && (
        <Kpi icon="🧾" titulo="F29 por declarar" to="/contabilidad?tab=libro"
          loading={ld.f29} urgente={!!(f29 && f29Vencido)}
          valor={f29 ? clpC(f29.total != null ? f29.total : f29.iva) : "—"}
          color={f29Color} sub={f29Sub}
          title={f29 ? `Borrador F29 ${f29.periodo} · IVA determinado ${clp(f29.iva)}${f29.total != null ? ` · total con PPM y retenciones ${clp(f29.total)}` : " · total pendiente de tasa PPM"}` : undefined} />
        )}

        {/* 3 · Pagos de la semana */}
        {esDe("contable", "pagos") && (
        <Kpi icon="💸" titulo="Pagos esta semana" to="/contabilidad?tab=pagos"
          loading={ld.pagos} urgente={pagosUrgente}
          valor={clpC(porPagarSemana)} color={pagosColor}
          sub={pa ? `${pa.venc.n} vencida${pa.venc.n === 1 ? "" : "s"} (${clpC(pa.venc.monto)}) · ${pa.pv.n} por vencer 7d` : "pagos a proveedores —"}
          title={pa ? `Vencidas ${clp(pa.venc.monto)} + por vencer en 7 días ${clp(pa.pv.monto)}` : undefined} />
        )}

        {/* 4 · Conciliación bancaria */}
        <Kpi icon="🏦" titulo="Cartolas por conciliar" to="/contabilidad?tab=cartolas"
          loading={ld.cartola}
          valor={ca ? ca.pend : "—"}
          color={ca ? (ca.pend > 0 ? SEM.amber : SEM.green) : "#fff"}
          sub={caSub}
          title="Movimientos de cartola sin documento conciliado — se resuelven en Contabilidad" />

        {/* 5 · Liquidaciones del mes */}
        <Kpi icon="👷" titulo={`Liquidaciones ${mesActualNombre}`} to="/liquidaciones"
          loading={ld.liq}
          valor={liqValor} color={liqColor} sub={liqSub}
          title="Liquidaciones de sueldo del mes: generar → aprobar → pagar" />

        {/* 6 · Facturas de compra sin vincular (conciliador) */}
        <Kpi icon="📄" titulo="Compras sin vincular" to="/contabilidad?tab=compras&filtro=sin-vincular"
          loading={ld.vinc}
          valor={vi ? vi.n : "—"}
          color={vi ? (vi.n > 0 ? SEM.amber : SEM.green) : "#fff"}
          sub={vi ? (vi.n > 0 ? `de ${vi.total} compra${vi.total === 1 ? "" : "s"} del mes · ${clpC(vi.monto)} sin OC/OT` : "todas las compras del mes vinculadas") : "conciliador de compras —"}
          title="Facturas de compra del mes sin orden de compra ni OT vinculada (las NC no se vinculan)" />
      </div>
    </div>
  );
};

export default PulsoContable;
